<!--@format-->
<template>
    <div class="client-solutions-roadmap-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Information</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="widget-container">
                    <div class="main-title">
                        <h3>Client Solutions Roadmap</h3>
                    </div>
                    <div class="graph">
                        <div class="values">
                            <div class="percentage">{{ servicePercent | percentage }}</div>
                            <div>
                                <span class="value"
                                    ><span class="sold">{{ totalSold | roundedCurrency }}</span>
                                    sold</span
                                >
                                /
                                <span class="value"
                                    ><span class="potential">{{
                                        totalPotential | roundedCurrency
                                    }}</span>
                                    potential</span
                                >
                                /
                                <span class="value"
                                    ><span class="total">{{
                                        servicesTotal | roundedCurrency
                                    }}</span>
                                    total</span
                                >
                            </div>
                        </div>
                        <div class="progress-bar">
                            <div
                                class="goal-progress"
                                :style="{ width: `${servicePercent * 100}%` }"
                            ></div>
                        </div>
                    </div>
                    <div class="sold-services">
                        <div class="heading">
                            <span class="title circle circle-green-gradient-bg">Sold Services</span>
                            <div>
                                <span class="sold">{{ totalSold | roundedCurrency }}</span> a month
                            </div>
                        </div>
                        <div class="services-wrapper" v-if="hasSoldServices">
                            <div
                                v-for="service in soldServices"
                                :key="service.id"
                                class="service-row"
                            >
                                <div class="service-name">{{ service.name }}</div>
                                <div class="service-value">
                                    <span class="sold">{{ service.total | roundedCurrency }}</span>
                                    a month
                                </div>
                            </div>
                        </div>
                        <div class="services-wrapper no-services-wrapper" v-else>
                            No services sold to this company
                        </div>
                    </div>
                    <div class="potential-services">
                        <div class="heading">
                            <span class="title circle circle-red-gradient-bg"
                                >Potential Services</span
                            >
                            <div>
                                <span class="potential">{{
                                    totalPotential | roundedCurrency
                                }}</span>
                                a month
                            </div>
                        </div>
                        <div class="services-wrapper" v-if="hasPotentialServices">
                            <div
                                v-for="service in potentialServices"
                                :key="service.id"
                                class="service-row"
                            >
                                <div class="service-name">{{ service.name }}</div>
                                <div class="service-value">
                                    <span class="potential">{{
                                        service.total | roundedCurrency
                                    }}</span>
                                    a month
                                </div>
                            </div>
                        </div>
                        <div class="services-wrapper no-services-wrapper" v-else>
                            No services to display
                        </div>
                        <div class="actions">
                            <app-button size="small" @click="openCompanyWidget"
                                >Assign Sold Services +</app-button
                            >
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <app-button class="save-btn" @click="closeWidget"> Go Back </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'client-solutions-roadmap-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            services: [],
            employeeCount: 0,
            deviceCount: 0,
        };
    },

    computed: {
        soldServices() {
            return this.services.filter((p) => p.hasService);
        },

        hasSoldServices() {
            return this.soldServices.length > 0;
        },

        potentialServices() {
            return this.services.filter((p) => !p.hasService);
        },

        hasPotentialServices() {
            return this.potentialServices.length > 0;
        },

        totalSold() {
            var soldValues = this.soldServices.map((a) => a.total);
            return soldValues.reduce((a, b) => a + b, 0);
        },

        totalPotential() {
            var potentialValues = this.potentialServices.map((a) => a.total);
            return potentialValues.reduce((a, b) => a + b, 0);
        },

        servicesTotal() {
            return this.totalSold + this.totalPotential;
        },

        servicePercent() {
            if (this.servicesTotal > 0) {
                return this.totalSold / this.servicesTotal;
            } else {
                return 0;
            }
        },
    },

    created() {
        this.companyId = this.widget.options.companyId;
        this.getData();

        EventBus.$on('company:update', this.getData);
    },

    beforeDestroy() {
        EventBus.$off('company:update', this.getData);
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        openCompanyWidget() {
            EventBus.$emit('open-widget', {
                name: 'company-edit-widget',
                options: {
                    companyId: this.companyId,
                },
            });
        },

        getData() {
            this.loading = true;

            this.getCompanyDetails()
                .then(this.getServices)
                .then(() => {
                    this.loading = false;
                });
        },

        getCompanyDetails() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Companies?$filter=Id eq ${this.companyId}&$select=ClientId,ApproximateEmployeeCount,DeviceCount`
                    )
                    .then((response) => {
                        this.clientId = response.data.value[0].ClientId;
                        this.employeeCount = response.data.value[0].ApproximateEmployeeCount || 0;
                        this.deviceCount = response.data.value[0].DeviceCount || 0;

                        resolve();
                    });
            });
        },

        getServices() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Services?$filter=ClientId eq ${this.clientId}&$select=Id,Name,ServiceBillingType,BillingCycle,Price&$expand=CompanyServices($filter=CompanyId eq ${this.companyId})`
                    )
                    .then((response) => {
                        this.services = response.data.value.map((item) => {
                            var billingCycle = item.BillingCycle || 1;
                            var total = 0;

                            switch (item.ServiceBillingType) {
                                case 'PerEmployee':
                                    total = (this.employeeCount * item.Price) / billingCycle;
                                    break;
                                case 'PerDevice':
                                    total = (this.deviceCount * item.Price) / billingCycle;
                                    break;
                                case 'Fixed':
                                    total = item.Price / billingCycle;
                                    break;
                            }

                            return {
                                id: item.Id,
                                hasService: item.CompanyServices.length > 0,
                                name: item.Name,
                                billingCycle: billingCycle,
                                price: item.Price,
                                total: total,
                            };
                        });

                        resolve();
                    });
            });
        },
    },
};
</script>

<style>
.client-solutions-roadmap-widget .input-label {
    font-weight: 600;
    display: flex;
    align-items: center;
}

.client-solutions-roadmap-widget .action-container {
    display: flex;
}

.client-solutions-roadmap-widget .action-container .save-btn {
    width: 128px;
}

.client-solutions-roadmap-widget .action-container .back-btn {
    width: 128px;
    background: transparent;
    color: #18aeff;
    margin-right: 10px;
}

.client-solutions-roadmap-widget .action-container .back-btn:hover {
    background: #e1e0e5;
}

.client-solutions-roadmap-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.client-solutions-roadmap-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.client-solutions-roadmap-widget .error {
    color: #ff0000;
    font-size: 14px;
}

.client-solutions-roadmap-widget .app-input label {
    display: none;
}

.client-solutions-roadmap-widget .error-message {
    display: none;
}

.client-solutions-roadmap-widget .widget-contents-container .content-create {
    padding: 0;
}

.client-solutions-roadmap-widget .content-create .app-text-editor-wrapper,
.client-solutions-roadmap-widget .content-create .app-text-editor,
.client-solutions-roadmap-widget .content-create .widget-container {
    height: 100%;
}

.client-solutions-roadmap-widget .content-create .widget-container .controls {
    display: none;
}

.client-solutions-roadmap-widget.widget .widget-footer {
    justify-content: flex-end;
}
.client-solutions-roadmap-widget .graph {
    overflow: hidden;
    margin: 25px 0px;
}
.client-solutions-roadmap-widget .progress-bar {
    width: 100%;
    background: #e9e9ef;
    border-radius: 10px;
    height: 10px;
    position: relative;
    margin-bottom: 3px;
}

.client-solutions-roadmap-widget .progress-bar .goal-progress {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    border-radius: 10px;
    background: #28da35;
    background: linear-gradient(to right, #28da35 0%, #15eca4 100%);
}

.client-solutions-roadmap-widget .percentage {
    font: normal normal medium 14px/14px Roboto;
    font-weight: 400;
}

.client-solutions-roadmap-widget .graph .values {
    display: flex;
    justify-content: space-between;
}

.client-solutions-roadmap-widget .sold,
.client-solutions-roadmap-widget .percentage {
    color: #28da35;
}

.client-solutions-roadmap-widget .potential {
    color: #f30070;
}

.client-solutions-roadmap-widget .service-row {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    margin: 20px;
}

.client-solutions-roadmap-widget .services-wrapper {
    border: 1px solid #cecece;
    border-radius: 19px;
    margin: 0.5em 0 1em;
}

.client-solutions-roadmap-widget .service-row {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
}

.client-solutions-roadmap-widget .heading {
    display: flex;
    justify-content: space-between;
    padding-left: 25px;
}

.client-solutions-roadmap-widget .circle::before {
    top: 1px;
}

.client-solutions-roadmap-widget .heading .title {
    font-weight: 500;
}

.client-solutions-roadmap-widget .no-services-wrapper {
    padding: 20px;
    font-size: 14px;
}

.client-solutions-roadmap-widget .actions {
    display: flex;
    justify-content: flex-end;
}

.client-solutions-roadmap-widget .graph .values {
    font-weight: 400;
}

.client-solutions-roadmap-widget .sold-services .heading .sold,
.client-solutions-roadmap-widget .potential-services .heading .potential {
    font-weight: 500;
    font-size: 18px;
}
</style>
